import { useEffect, useState, useContext, useMemo } from "react";
import "./style.scss";
import { mapDispatchToProps, mapStateToProps } from "../../../store/dispatcher";
import { DispatchProps, StateProps } from "../../../store";
import { connect } from "react-redux";
import AuthContext from "../../../contexts/AuthContext";
import dayjs from "dayjs";

import axios from "../../../services/axios";
import { useParams } from "react-router-dom";
import { Segmented, Skeleton, Tooltip, Switch, Button } from "antd";
import {
  DatabaseOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import TimeSelector, { generate_period } from "./time_selector/TimeSelector";
import { useTranslation } from "react-i18next";
import GraphLoader from "./graph_loader/GraphLoader";
import { Device, isDeviceDetailLoaded } from "../../../lib/Device";
import { darkenHexColor } from "../../../helpers/color";
import PowerLineChart from "./line_chart/PowerLineChart";
import TensionLineChart from "./line_chart/TensionLineChart";
import TemperatureLineChart from "./line_chart/TemperatureLineChart";
import {
  timestampToReadable,
  downRounding,
  upRounding,
} from "./line_chart/LineChartCommon";
import { Home } from "../../../lib/Home";
import DropoutLineChart from "./line_chart/DropoutLineChart";
import { SCHEDULE_STANDBY } from "../../../lib/planningSchedule";

import { getTimezonesForCountry, CountryCode } from "countries-and-timezones";

const generate_line_data = (
  data: any,
  currentDevice: Device,
  currentHome: Home
) => {
  const tempIdxToTemp: Record<0 | 1 | 2 | 3, number> = {
    0: currentDevice.t_cold,
    1: currentDevice.t_lukewarm,
    2: currentDevice.t_hot,
    3: currentDevice.t_very_hot,
  };
  const homeTimezone = getTimezonesForCountry(
    currentHome.country as CountryCode
  )[0].name;
  const chart_data = data.map((entry: any) => {
    const entryDate = dayjs.unix(entry.timestamp).tz(homeTimezone);
    const entryDay = entryDate.day();
    const dayIdx = entryDay > 0 ? entryDay - 1 : 6;
    const hourIdx = entryDate.hour();
    const tempIdx = currentDevice.schedule?.[dayIdx]?.[hourIdx] ?? -1;
    const minPlanningTemp =
      tempIdx >= 0 && tempIdx <= 3
        ? tempIdxToTemp[tempIdx as 0 | 1 | 2 | 3]
        : tempIdxToTemp[0];

    const circulatorLineValue = entry.circulator_state
      ? (entry.circulator_state.toUpperCase() === "ENABLED" ? 50 : 0)
      : 0;

    return {
      time: entry.timestamp,
      power_balance: entry.power_balance,
      power_balance_no_soliseco: entry.power_balance_no_soliseco,
      soliseco_power: entry.soliseco_power,
      p_1_soliseco: entry.p_1_soliseco,
      p_2_soliseco: entry.p_2_soliseco,
      p_3_soliseco: entry.p_3_soliseco,
      u_1: entry.u_1,
      u_2: entry.u_2,
      u_3: entry.u_3,
      water_temperature: entry.water_temperature,
      dropout_v_max: currentDevice.dropout_v_max,
      dropout_v_max_l2: currentDevice.dropout_v_max_l2,
      dropout_v_max_l3: currentDevice.dropout_v_max_l3,
      dropout_v_threshold: currentDevice.dropout_v_threshold,
      min_planning_temp: minPlanningTemp,
      schedule_standby_mask: tempIdx === SCHEDULE_STANDBY ? 1 : null,
      circulator_state: entry.circulator_state, 
      circulator_line: circulatorLineValue, 
      valve_position: entry.valve_position,
    };
  });
  return chart_data;
};

const smart_rounding = (val: number): number => {
  return parseFloat(val >= 100 ? val.toFixed(1) : val.toFixed(2));
};

const DEFAULT_SAVING_PER_SELF_CONSUMED_KWH = 0.25;
const DEFAULT_SELLING_PRICE_EXPORTED_KWH = 0.08;
const DEFAULT_BUYING_PRICE_IMPORTED_KWH = 0.3;

const getEffectivePrices = (home: Home | undefined) => ({
  saving: home?.saving_per_self_consumed_kwh != null
    ? parseFloat(String(home.saving_per_self_consumed_kwh))
    : DEFAULT_SAVING_PER_SELF_CONSUMED_KWH,
  selling: home?.selling_price_exported_kwh != null
    ? parseFloat(String(home.selling_price_exported_kwh))
    : DEFAULT_SELLING_PRICE_EXPORTED_KWH,
  buying: home?.buying_price_imported_kwh != null
    ? parseFloat(String(home.buying_price_imported_kwh))
    : DEFAULT_BUYING_PRICE_IMPORTED_KWH,
});

const toEur = (kwh: number, pricePerKwh: number) => smart_rounding(kwh * pricePerKwh);

const generate_pie_data = (
  data: any,
  multiDeviceHome: boolean,
  displayUnit: "kWh" | "eur" = "kWh",
  prices?: { saving: number; selling: number; buying: number }
) => {
  const toVal = (kwh: number, price: number) =>
    displayUnit === "eur" && prices ? toEur(kwh, price) : smart_rounding(kwh);

  const saving = prices?.saving ?? DEFAULT_SAVING_PER_SELF_CONSUMED_KWH;
  const selling = prices?.selling ?? DEFAULT_SELLING_PRICE_EXPORTED_KWH;
  const buying = prices?.buying ?? DEFAULT_BUYING_PRICE_IMPORTED_KWH;

  const exported_energy_values = [
    {
      name: "real_export",
      value: toVal(data.energy_export, selling),
      color: "#D89537",
    },
    {
      name: "device_solar_energy_export",
      value: toVal(data.solar_energy_soliseco.self, saving),
      color: "#72A363",
    },
  ];
  if (multiDeviceHome === true) {
    exported_energy_values.push({
      name: "other_solar_energy_export",
      value: toVal(data.solar_energy_soliseco.others, saving),
      color: "#3b6bb8",
    });
  }
  const exported_energy = {
    total: displayUnit === "eur" && prices
      ? smart_rounding(
          exported_energy_values.reduce((sum, v) => sum + v.value, 0)
        )
      : smart_rounding(
          data.energy_export +
            data.solar_energy_soliseco.self +
            data.solar_energy_soliseco.others
        ),
    values: exported_energy_values,
  };

  const otherImportKwh = data.energy_import -
    (data.non_solar_energy_soliseco.self + data.non_solar_energy_soliseco.others);
  const imported_energy_values = [
    {
      name: "other_import",
      value: toVal(otherImportKwh, buying),
      color: "#a63d50",
    },
    {
      name: "device_import",
      value: toVal(data.non_solar_energy_soliseco.self, buying),
      color: "#041a1c",
    },
  ];
  if (multiDeviceHome === true) {
    imported_energy_values.push({
      name: "other_device_import",
      value: toVal(data.non_solar_energy_soliseco.others, buying),
      color: "#3b6bb8",
    });
  }
  const imported_energy = {
    total: displayUnit === "eur" && prices
      ? smart_rounding(
          imported_energy_values.reduce((sum, v) => sum + v.value, 0)
        )
      : smart_rounding(data.energy_import),
    values: imported_energy_values,
  };

  const hot_water_values = [
    {
      name: "water_production_legend_1",
      value: toVal(data.solar_energy_soliseco.self, saving),
      color: "#72A363",
    },
    {
      name: "water_production_legend_2",
      value: toVal(data.non_solar_energy_soliseco.self, buying),
      color: "#041a1c",
    },
  ];
  const hot_water_production = {
    total: displayUnit === "eur" && prices
      ? smart_rounding(hot_water_values.reduce((sum, v) => sum + v.value, 0))
      : smart_rounding(
          data.solar_energy_soliseco.self + data.non_solar_energy_soliseco.self
        ),
    values: hot_water_values,
  };

  return { exported_energy, imported_energy, hot_water_production };
};

const RADIAN = Math.PI / 180;

interface LabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
  value: number;
  fill: string;
  index: number;
}

const createPieLabelRenderer = (displayUnit: "kWh" | "eur") => ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
  fill,
  index,
}: LabelProps) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + (radius + 25) * Math.cos(-midAngle * RADIAN);
  const y = cy + (radius + 25) * Math.sin(-midAngle * RADIAN);
  const label = displayUnit === "eur" ? `€${value}` : `${value} kWh`;

  return (
    <text
      x={x}
      y={y}
      fill={fill}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {label}
    </text>
  );
};

const TemperatureCell = ({
  device,
  temperature,
}: {
  device: any;
  temperature: number;
}) => {
  const { t } = useTranslation("settings");

  const color = {
    cold: "#D1E6FF",
    warm: "#FCECCD",
    hot: "#F9DBBF",
    very_hot: "#FCCDCD",
    overheat: "#FC4646FF",
  };

  const temp_to_show: "cold" | "warm" | "hot" | "very_hot" | "overheat" =
    useMemo(() => {
      if (!device) return "cold";

      if (temperature >= device.t_max) {
        return "overheat";
      } else if (temperature >= device.t_very_hot) {
        return "very_hot";
      } else if (temperature >= device.t_hot) {
        return "hot";
      } else if (temperature >= device.t_lukewarm) {
        return "warm";
      } else {
        return "cold";
      }
    }, [temperature, device]);

  return (
    <Tooltip title={temperature?.toFixed(1) + "°C"}>
      <span
        className="value cell"
        style={{
          color: darkenHexColor(color[temp_to_show]),
          backgroundColor: color[temp_to_show],
        }}
      >
        {t(temp_to_show)}
      </span>
    </Tooltip>
  );
};

/**
 * Normalizes WiFi strength string for translation keys
 * @param wifiStrength - String from device like "GOOD", "FAIR", etc.
 * @returns Normalized string in lowercase with underscore: "none", "very_weak", "weak", "fair", "good", "excellent"
 */
const normalizeWifiStrength = (wifiStrength: string | undefined | null): string | null => {
  if (wifiStrength === undefined || wifiStrength === null || typeof wifiStrength !== "string") {
    return null;
  }

  if (wifiStrength.trim() === "") {
    return null;
  }

  // Convert to lowercase for translation key (e.g., "GOOD" -> "good")
  return wifiStrength.toLowerCase();
};

const WifiStrengthCell = ({
  wifiStrength,
}: {
  wifiStrength: string;
}) => {
  const { t } = useTranslation("dashboard");

  const color = {
    none: "#FC4646FF",       
    very_weak: "#FC7A7A",   
    weak: "#FFA500",         
    fair: "#FFD700",      
    good: "#90EE90",        
    excellent: "#32CD32",     
  };

  const strength_to_show: "none" | "very_weak" | "weak" | "fair" | "good" | "excellent" =
    useMemo(() => {
      const normalized = normalizeWifiStrength(wifiStrength);
      if (!normalized) return "none";

      if (normalized === "none") return "none";
      if (normalized === "very_weak") return "very_weak";
      if (normalized === "weak") return "weak";
      if (normalized === "fair") return "fair";
      if (normalized === "good") return "good";
      if (normalized === "excellent") return "excellent";
      
      return "none";
    }, [wifiStrength]);

  return (
    <span
      className="value cell"
      style={{
        color: darkenHexColor(color[strength_to_show]),
        backgroundColor: color[strength_to_show],
      }}
    >
      {t(`wifi_strength_${strength_to_show}`)}
    </span>
  );
};

const StateCell = ({
  state,
}: {
  state: string | null | undefined;
}) => {
  const { t } = useTranslation("dashboard");

  if (!state) return <span className="value">-</span>;

  const normalizedState = state.toUpperCase();
  const isEnabled = normalizedState === "ENABLED";

  const color = isEnabled ? "#90EE90" : "#FC7A7A"; 

  return (
    <span
      className="value cell"
      style={{
        color: darkenHexColor(color),
        backgroundColor: color,
        textTransform: "lowercase",
      }}
    >
      {normalizedState}
    </span>
  );
};

const ValveFeedbackCell = ({
  feedback,
}: {
  feedback: string | null | undefined;
}) => {
  const { t } = useTranslation("dashboard");

  if (!feedback) return <span className="value">-</span>;

  const normalizedFeedback = feedback.toUpperCase();
  const isOpen = normalizedFeedback === "OPEN";

  const color = isOpen ? "#90EE90" : "#FC7A7A";

  return (
    <span
      className="value cell"
      style={{
        color: darkenHexColor(color),
        backgroundColor: color,
        textTransform: "lowercase",
      }}
    >
      {normalizedFeedback === "CLOSE" ? "CLOSED" : normalizedFeedback}
    </span>
  );
};

const ValvePositionCell = ({
  position,
}: {
  position: string | null | undefined;
}) => {
  const { t } = useTranslation("dashboard");

  if (!position) return <span className="value">-</span>;

  const normalizedPosition = position.toUpperCase();

  const colorMap: { [key: string]: string } = {
    "WATER_HEATER": "#D1E6FF",  
    "HEATER": "#FCECCD",    
    "FULL_SOLAR": "#90EE90",      
    "HYBRID": "#FFD700",          
  };

  const color = colorMap[normalizedPosition] || "#E0E0E0";

  return (
    <span
      className="value cell"
      style={{
        color: darkenHexColor(color),
        backgroundColor: color,
        textTransform: "lowercase",
      }}
    >
      {normalizedPosition.replace(/_/g, " ")}
    </span>
  );
};


const DeviceDashboard = ({
  devices,
  homes,
}: {
  devices: Device[];
  homes: Home[];
}) => {
  const { device_id, home_id } = useParams();
  const { t } = useTranslation("dashboard");

  const [lastData, setLastData] = useState<any>(null);
  const [lineData, setLineData] = useState<any>(null);
  const [rawEnergyData, setRawEnergyData] = useState<any>(null);
  const [displayUnit, setDisplayUnit] = useState<"kWh" | "eur">("kWh");

  const pieI18nKey = (baseKey: string) =>
    displayUnit === "eur" ? `${baseKey}_eur` : baseKey;

  const [focusMode, setFocusMode] = useState<boolean>(true);
  const [lastDataSegment, setLastDataSegment] = useState<string>("SOLISECO");
  const [pieDataPeriod, setPieDataPeriod] = useState<any>(
    generate_period(new Date(), "now")
  );
  const [lineDataSegment, setLineDataSegment] = useState<string>("POWERS");
  const [lineDataPeriod, setLineDataPeriod] = useState<any>(
    generate_period(new Date(), "now")
  );

  const { user } = useContext(AuthContext);

  const currentDevice = devices?.find(
    (device) => device.id === parseInt(device_id ? device_id : "0")
  );

  const daysSinceSanitation = (
    lastSanitationTimestamp: number | undefined | null
  ): string => {
    if (!lastSanitationTimestamp || lastSanitationTimestamp === 0) {
      return t("no_sanitation_yet");
    }
    const secondsSince = Date.now() / 1000 - lastSanitationTimestamp;
    const days = Math.floor(secondsSince / 86400);
    return `${days} ${t("days")}`;
  };

  const currentHome = homes?.find(
    (home) => home.id === parseInt(home_id ? home_id : "0")
  );

  const multiDeviceHome: boolean = useMemo(() => {
    if (home_id && devices)
      return (
        devices.filter((device) => device.home === parseInt(home_id)).length > 1
      );
    return false;
  }, [devices, home_id]);

  const pieData = useMemo(() => {
    if (!rawEnergyData) return null;
    const prices = getEffectivePrices(currentHome ?? undefined);
    return generate_pie_data(
      rawEnergyData,
      multiDeviceHome,
      displayUnit,
      displayUnit === "eur" ? prices : undefined
    );
  }, [rawEnergyData, multiDeviceHome, displayUnit, currentHome]);

  const lineChartXTickFormatter = (val: string, idx: number) => {
    if (!currentHome) {
      return "";
    }
    const formattedDate = timestampToReadable(
      parseInt(val),
      currentHome.country as CountryCode
    );
    if (lineDataPeriod && lineDataPeriod.start === lineDataPeriod.end) {
      return formattedDate.split(" ")[1];
    }
    return formattedDate;
  };

  const ticks = useMemo(() => {
    if (lineData) {
      if (lineData.length > 1) {
        const start = lineData[0].time;
        const end = lineData[lineData.length - 1].time;
        const roundedStart = upRounding(start, 900);
        const roundedEnd = downRounding(end, 900);
        const diff = roundedEnd - roundedStart;
        if (diff > 3600) {
          const secondTick = downRounding(roundedStart + diff / 3, 900);
          const thirdTick = downRounding(roundedStart + (2 * diff) / 3, 900);
          return [roundedStart, secondTick, thirdTick, roundedEnd];
        }
        if (roundedEnd - roundedStart >= 900) {
          return [roundedStart, roundedEnd];
        }
      }
      return lineData.map((elem: any) => elem.time);
    }
    return [];
  }, [lineData]);

  const isTelemetryFieldVisible = (field: string): boolean => {
    if (!currentDevice) {
      return true;
    }

    const notApplicableParams: string[] =
      currentDevice.not_applicable_device_parameters ||
      currentDevice.parameter_configuration?.not_applicable_device_parameters ||
      [];

    const advancedParams: string[] =
      currentDevice.advanced_device_parameters ||
      currentDevice.parameter_configuration?.advanced_device_parameters ||
      [];

    const isBasicUser = !user.is_installer && !user.is_advanced_user;
    const isAdmin = user.is_staff || user.is_superuser;

    if (notApplicableParams.includes(field)) {
      return false;
    }

    if (advancedParams.includes(field) && isBasicUser && !isAdmin) {
      return false;
    }

    return true;
  };

  const update_pie_data = async (period: any) => {
    await axios
      .get(
        `/api-v1/device/${device_id}/energy?start=${period.start}&end=${period.end}`
      )
      .then((resp) => {
        setRawEnergyData(resp.data);
      })
      .catch((e) => setRawEnergyData(null));
  };

  const update_line_data = async (
    period: any,
    currentDevice: Device,
    currentHome: Home
  ) => {
    await axios
      .get(
        `/api-v1/device/${device_id}/time-data?start=${period.start}&end=${period.end}`
      )
      .then((resp) => {
        setLineData(generate_line_data(resp.data, currentDevice, currentHome));
      })
      .catch((e) => setLineData([]));
  };

  const update_last_data = async (device_id: string) => {
    await axios
      .get(`/api-v1/device/${device_id}/last`)
      .then((resp) => {
        setLastData(resp.data);
        console.log("last data", resp.data);
      })
      .catch((e) => setLastData({}));
  };

  const refetch = () => {
    if (device_id && currentDevice && currentHome) {
      update_last_data(device_id);
      update_pie_data(pieDataPeriod);
      update_line_data(lineDataPeriod, currentDevice, currentHome);
    }
  };

  const isFocus = useMemo(() => {
    return user.is_login && document.visibilityState === "visible";
  }, [user.is_login, document.visibilityState]);

  const focus = (e: any) => {
    if (isFocus) {
      refetch();
    }
  };

  useEffect(() => {
    window.addEventListener("visibilitychange", focus);
    return () => {
      window.removeEventListener("visibilitychange", focus);
    };
  }, [device_id, currentHome, pieDataPeriod, lineDataPeriod]);

  useEffect(() => {
    if (device_id && isFocus) {
      update_last_data(device_id);
      const intervalId = setInterval(
        () => update_last_data(device_id),
        1000 * 120
      );
      return () => clearInterval(intervalId);
    }
  }, [device_id, isFocus]);

  useEffect(() => {
    if (device_id && isFocus) {
      update_pie_data(pieDataPeriod);
      const intervalId = setInterval(
        () => update_pie_data(pieDataPeriod),
        1000 * 120
      );
      return () => clearInterval(intervalId);
    }
  }, [device_id, pieDataPeriod, isFocus]);

  useEffect(() => {
    if (
      currentDevice &&
      isDeviceDetailLoaded(currentDevice) &&
      device_id &&
      isFocus &&
      currentHome
    ) {
      update_line_data(lineDataPeriod, currentDevice, currentHome);
      const intervalId = setInterval(
        () => update_line_data(lineDataPeriod, currentDevice, currentHome),
        1000 * 120
      );
      return () => clearInterval(intervalId);
    }
  }, [device_id, currentDevice, currentHome, lineDataPeriod, isFocus]);

  return (
    <div className="device-dashboard">
      <div className="pie-container">
        <div className="pie-container__toolbar">
          <Switch
            checked={displayUnit === "eur"}
            onChange={(checked) => setDisplayUnit(checked ? "eur" : "kWh")}
            checkedChildren={t("dashboard_display_eur")}
            unCheckedChildren={t("dashboard_display_kwh")}
          />
        </div>
        <div className="pies">
          <div className="pie-block">
            <span className="pie-title">
              {t(pieI18nKey("exported_energy_title"))}{" "}
              <Tooltip title={t(pieI18nKey("exported_energy_tooltip"))}>
                <InfoCircleOutlined
                  style={{ color: "#012124", opacity: 0.4 }}
                />
              </Tooltip>
            </span>
            {pieData != null ? (
              <>
                <div className="chart-container">
                  <div className="absolute-center-title">
                    <span className="title">
                      {displayUnit === "eur"
                        ? `€${pieData.exported_energy.total}`
                        : `${pieData.exported_energy.total}kWh`}
                    </span>
                    <span>{t(pieI18nKey("exported_energy_center_data"))}</span>
                  </div>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData.exported_energy.values}
                        innerRadius={60}
                        outerRadius={75}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={createPieLabelRenderer(displayUnit)}
                      >
                        {pieData.exported_energy.values.map(
                          (entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          )
                        )}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="legend-container">
                  {pieData.exported_energy.values.map(
                    (entry: any, index: number) => (
                      <div className="legend-item" key={index}>
                        <span
                          className="color-block"
                          style={{ backgroundColor: entry.color }}
                        ></span>
                        <span>
                          {t(pieI18nKey(entry.name), {
                            device_name: currentDevice?.name,
                          })}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </>
            ) : (
              <GraphLoader shape="circle" />
            )}
          </div>
          <div className="pie-block">
            <span className="pie-title">
              {t(pieI18nKey("imported_energy_title"))}{" "}
              <Tooltip title={t(pieI18nKey("imported_energy_tooltip"))}>
                <InfoCircleOutlined
                  style={{ color: "#012124", opacity: 0.4 }}
                />
              </Tooltip>
            </span>
            {pieData != null ? (
              <>
                <div className="chart-container">
                  <div className="absolute-center-title">
                    <span className="title">
                      {displayUnit === "eur"
                        ? `€${pieData.imported_energy.total}`
                        : `${pieData.imported_energy.total}kWh`}
                    </span>
                    <span>{t(pieI18nKey("imported_energy_center_data"))}</span>
                  </div>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData.imported_energy.values}
                        innerRadius={60}
                        outerRadius={75}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={createPieLabelRenderer(displayUnit)}
                      >
                        {pieData.imported_energy.values.map(
                          (entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          )
                        )}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="legend-container">
                  {pieData.imported_energy.values.map(
                    (entry: any, index: number) => (
                      <div className="legend-item" key={index}>
                        <span
                          className="color-block"
                          style={{ backgroundColor: entry.color }}
                        ></span>
                        <span>
                          {t(pieI18nKey(entry.name), {
                            device_name: currentDevice?.name,
                          })}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </>
            ) : (
              <GraphLoader shape="circle" />
            )}
          </div>
          <div className="pie-block">
            <span className="pie-title">
              {t(pieI18nKey("water_production_title"))}{" "}
              <Tooltip title={t(pieI18nKey("water_production_tooltip"))}>
                <InfoCircleOutlined
                  style={{ color: "#012124", opacity: 0.4 }}
                />
              </Tooltip>
            </span>
            {pieData != null ? (
              <>
                <div className="chart-container">
                  <div className="absolute-center-title">
                    <span className="title">
                      {displayUnit === "eur"
                        ? `€${pieData.hot_water_production.total}`
                        : `${pieData.hot_water_production.total}kWh`}
                    </span>
                    <span>{t(pieI18nKey("water_production_center_data"))}</span>
                  </div>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData.hot_water_production.values}
                        innerRadius={60}
                        outerRadius={75}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={createPieLabelRenderer(displayUnit)}
                      >
                        {pieData.hot_water_production.values.map(
                          (entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          )
                        )}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="legend-container">
                  {pieData.hot_water_production.values.map(
                    (entry: any, index: number) => (
                      <div className="legend-item" key={index}>
                        <span
                          className="color-block"
                          style={{ backgroundColor: entry.color }}
                        ></span>
                        <span>
                          {t(pieI18nKey(entry.name), {
                            device_name: currentDevice?.name,
                          })}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </>
            ) : (
              <GraphLoader shape="circle" />
            )}
          </div>
        </div>
        <TimeSelector set_period={setPieDataPeriod} />
      </div>
      <div className="row">
        <div className="chart-title-block">
          <span className="title">
            {t("last_info_title")}
            <Tooltip title={t("last_info_title")}>
              <InfoCircleOutlined style={{ color: "#012124", opacity: 0.4 }} />
            </Tooltip>
          </span>

          <div
            className={`chart-block data ${
              lastData != null && Object.keys(lastData).length === 0
                ? "empty"
                : ""
            }`}
          >
            {lastData != null &&
              (Object.keys(lastData).length === 0 ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    justifyContent: "center",
                    margin: "auto",
                    width: "100%",
                  }}
                >
                  <DatabaseOutlined
                    style={{ color: "#012124", opacity: 0.6, fontSize: 80 }}
                  />
                  <p>No Data</p>
                </div>
              ) : (
                <>
                  <Segmented
                    options={[
                      {
                        label: t("last_info_choice_soliseco"),
                        value: "SOLISECO",
                      },
                      { label: t("last_info_choice_meter"), value: "COMPTEUR" },
                    ]}
                    value={lastDataSegment}
                    onChange={setLastDataSegment}
                    style={{ width: "fit-content", marginInline: "auto" }}
                  />
                  {lastDataSegment === "COMPTEUR" && (
                    <>
                      <div className="data-item">
                        <span className="label">{t("last_info_p_tot")}</span>
                        <span className="value">{lastData.p_tot} W</span>
                      </div>
                      <div className="data-item">
                        <span className="label">{t("last_info_u1")}</span>
                        <span className="value">{lastData.u_1} V</span>
                      </div>
                      <div className="data-item">
                        <span className="label">{t("last_info_u2")}</span>
                        <span className="value">{lastData.u_2} V</span>
                      </div>
                      <div className="data-item">
                        <span className="label">{t("last_info_u3")}</span>
                        <span className="value">{lastData.u_3} V</span>
                      </div>
                      <div className="data-item">
                        <span className="label">{t("last_info_peak")}</span>
                        <span className="value">
                          {lastData.power_monthly_peak} W
                        </span>
                      </div>
                      <div className="data-item">
                        <span className="label">{t("last_info_avg")}</span>
                        <span className="value">
                          {lastData.power_avg_15_m} W
                        </span>
                      </div>
                      <div className="data-item">
                        <span className="label">
                          {t("last_info_dropout_threshold_counter")}
                        </span>
                        <span className="value">
                          {lastData.dropout_threshold_counter}
                        </span>
                      </div>
                      <div className="data-item">
                        <span className="label">{t("last_info_power_uncompensated")}</span>
                        <span className="value">{lastData.power_uncompensated} W</span>
                      </div>
                      {lastData.dongle_wifi_strength && (
                        <div className="data-item">
                          <span className="label">{t("last_info_dongle_wifi_strength")}</span>
                          <WifiStrengthCell wifiStrength={lastData.dongle_wifi_strength} />
                        </div>
                      )}
                    </>
                  )}
                  {lastDataSegment === "SOLISECO" && (
                    <>
                      <div className="data-item">
                        <span className="label">{t("last_info_power")}</span>
                        <span className="value">
                          {lastData.power_soliseco} W
                        </span>
                      </div>
                      <div className="data-item">
                        <span className="label">
                          {t("last_info_power_percentage")}
                        </span>
                        <span className="value">
                          {lastData.heating_percentage_power} %
                        </span>
                      </div>
                      <div className="data-item">
                        <span className="label">
                          {t("last_info_current_temp")}
                        </span>
                        <TemperatureCell
                          device={currentDevice}
                          temperature={lastData.water_temperature}
                        />
                      </div>
                      <div className="data-item">
                        <span className="label">
                          {t("last_info_status")}
                          <Tooltip 
                            title={
                              lastData.running_state?.toLowerCase() === "no_dongle" ? t("status_no_dongle_tooltip") :
                              lastData.running_state?.toLowerCase() === "active" ? t("status_active_tooltip") :
                              lastData.running_state?.toLowerCase() === "pre_active" ? t("status_pre_active_tooltip") :
                              lastData.running_state?.toLowerCase() === "boost" ? t("status_boost_tooltip") :
                              lastData.running_state?.toLowerCase() === "standby" ? t("status_standby_tooltip") :
                              t("status_unknown_tooltip")
                            }
                          >
                            <InfoCircleOutlined style={{ color: "#012124", opacity: 0.4, marginLeft: "4px" }} />
                          </Tooltip>
                        </span>
                        <span
                          className="value cell"
                          style={{ textTransform: "lowercase" }}
                        >
                          {lastData.running_state}
                        </span>
                      </div>
                      {/* <div className="data-item">
                        <span className="label">
                          {t("last_info_last_sanitation")}
                        </span>
                        <span className="value">
                          {lastData?.last_sanitation_days ? 
                            `${lastData.last_sanitation_days} ${t("days_ago")} (${display_date_locale(
                              new Date(Date.now() - lastData.last_sanitation_days * 24 * 60 * 60 * 1000),
                              t("js_local_date")
                            )})` 
                            : t("never")
                          }
                        </span>
                      </div> */}
                      {currentDevice?.sanitation_active && (
                        <div className="data-item">
                          <span className="label">
                            {t("last_info_days_since_sanitation")}
                          </span>
                          {/* <span className="value">{daysSinceSanitation(lastData?.last_sanitation)}</span> */}
                          <span className="value">
                            {lastData?.last_sanitation_days}
                          </span>
                        </div>
                      )}
                      {lastData.wifi_strength && (
                        <div className="data-item">
                          <span className="label">{t("last_info_wifi_strength")}</span>
                          <WifiStrengthCell wifiStrength={lastData.wifi_strength} />
                        </div>
                      )}
                      {isTelemetryFieldVisible("voltage_soliseco") && lastData.voltage_soliseco !== null && lastData.voltage_soliseco !== undefined && (
                        <div className="data-item">
                          <span className="label">{t("last_info_voltage_soliseco")}</span>
                          <span className="value">{lastData.voltage_soliseco} V</span>
                        </div>
                      )}
                      {isTelemetryFieldVisible("circulator_state") && lastData.circulator_state && (
                        <div className="data-item">
                          <span className="label">{t("last_info_circulator_state")}</span>
                          <StateCell state={lastData.circulator_state} />
                        </div>
                      )}
                      {isTelemetryFieldVisible("valve_state") && lastData.valve_state && (
                        <div className="data-item">
                          <span className="label">{t("last_info_valve_state")}</span>
                          <StateCell state={lastData.valve_state} />
                        </div>
                      )}
                      {isTelemetryFieldVisible("valve_position") && lastData.valve_position && (
                        <div className="data-item">
                          <span className="label">{t("last_info_valve_position")}</span>
                          <ValvePositionCell position={lastData.valve_position} />
                        </div>
                      )}
                      {isTelemetryFieldVisible("valve_feedback") && lastData.valve_feedback && (
                        <div className="data-item">
                          <span className="label">{t("last_info_valve_feedback")}</span>
                          <ValveFeedbackCell feedback={lastData.valve_feedback} />
                        </div>
                      )}
                    </>
                  )}
                </>
              ))}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
                height: "100%",
              }}
            >
              <Button
                href={`http://${currentDevice?.own_address_ip}`}
                target="_blank"
              >
                {t("live_result_visualisation")}
              </Button>
            </div>
          </div>
        </div>
        <div className="chart-title-block large">
          <span className="title">
            {t("time_data_title")}
            <Tooltip title={t("time_data_tooltip")}>
              <InfoCircleOutlined style={{ color: "#012124", opacity: 0.4 }} />
            </Tooltip>
          </span>
          <div className="chart-block center">
            <div className="line-chart-header">
              {lineDataSegment === "POWERS" && <div style={{ width: 61.2 }} />}
              <Segmented
                options={[
                  { label: t("time_data_choice_powers"), value: "POWERS" },
                  { label: t("time_data_choice_tensions"), value: "TENSIONS" },
                  {
                    label: t("time_data_choice_temperature"),
                    value: "TEMPERATURE",
                  },
                  {
                    label: t("time_data_choice_dropout"),
                    value: "DROPOUT",
                    disabled: !currentDevice?.handle_dropout,
                  },
                ]}
                value={lineDataSegment}
                onChange={setLineDataSegment}
                style={{ width: "fit-content", marginInline: "auto" }}
              />
              {lineDataSegment === "POWERS" && (
                <Switch
                  checked={focusMode}
                  onClick={(checked) => setFocusMode(checked)}
                  checkedChildren="focus"
                  unCheckedChildren="full"
                />
              )}
            </div>

            {lineData == null ? (
              <Skeleton.Avatar
                active
                className="line-skeleton"
                shape="square"
              />
            ) : lineData.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <LineChartOutlined
                  style={{ color: "#012124", opacity: 0.6, fontSize: 80 }}
                />
                <p>No Data</p>
              </div>
            ) : (
              <>
                {lineDataSegment === "POWERS" && (
                  <PowerLineChart
                    lineData={lineData}
                    focusMode={focusMode}
                    currentDevice={currentDevice}
                    ticks={ticks}
                    lineChartXTickFormatter={lineChartXTickFormatter}
                    isTelemetryFieldVisible={isTelemetryFieldVisible}
                  />
                )}
                {lineDataSegment === "TENSIONS" && (
                  <TensionLineChart
                    lineData={lineData}
                    currentDevice={currentDevice}
                    ticks={ticks}
                    lineChartXTickFormatter={lineChartXTickFormatter}
                    isTelemetryFieldVisible={isTelemetryFieldVisible}
                  />
                )}
                {lineDataSegment === "TEMPERATURE" && (
                  <TemperatureLineChart
                    lineData={lineData}
                    currentDevice={currentDevice}
                    ticks={ticks}
                    lineChartXTickFormatter={lineChartXTickFormatter}
                    isTelemetryFieldVisible={isTelemetryFieldVisible}
                  />
                )}
                {lineDataSegment === "DROPOUT" && (
                  <DropoutLineChart
                    lineData={lineData}
                    currentDevice={currentDevice}
                    ticks={ticks}
                    lineChartXTickFormatter={lineChartXTickFormatter}
                    isTelemetryFieldVisible={isTelemetryFieldVisible}
                  />
                )}
              </>
            )}
            <TimeSelector
              set_period={setLineDataPeriod}
              excludedPeriods={["month", "year"]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(DeviceDashboard);
